import { useState } from 'react';
import React from 'react';
import Image from 'next/image';
import BannerDefault from '@/assets/img/BannerDefault.svg';
import QRCodeFrame from '@/assets/img/qrcode-frame.svg';
import { Flip } from '@/components/Flip';
import { useInstitutionalPage } from '@/contexts/InstitutionalPageContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { formatCompanyAddress } from '@/utils/components/institutionalPage';
import { downloadFile } from '@/utils/dom';
import { handleErrorAsync } from '@/utils/handlers';
import { cnpjMask, phoneMask } from '@/utils/masks';
import { Check, Share } from '@mui/icons-material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Modal,
  Stack,
  styled,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ShareInstitutionalPageWrapper from './wrapper';

interface IShareInstitutionPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const RowInfos = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type VIEW = 'COMPANY_INFOS' | 'QRCODE';

export const ShareInstitutionPage = ({
  isOpen,
  onClose,
}: IShareInstitutionPageProps) => {
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { setIsLoading } = useLoading();
  const { company } = useInstitutionalPage();

  const [view, setView] = useState<VIEW>('COMPANY_INFOS');
  const [QRcode, setQRcode] = useState<string | undefined>();
  const [alreadyCopied, setAlreadyCopied] = useState(false);

  const isShowingQRcode = view === 'QRCODE';

  const generateQRCode = async () => {
    if (QRcode) return setView('QRCODE');

    const [response, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      return api.post(
        `/api/companies/${company.id}/qrcode/`,
        {},
        {
          responseType: 'arraybuffer',
        }
      );
    }).finally(() => setIsLoading(false));

    if (error) {
      return Toast('error', error.message);
    }

    setQRcode(response.data);
    setView('QRCODE');
  };

  const downloadQRCode = () => {
    if (!QRcode) return;

    downloadFile(
      Buffer.from(QRcode),
      `QRCode ${company.display_name || company.full_name}`,
      'png'
    );
  };

  const copyInstitutionalUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setAlreadyCopied(true);
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <>
        <Flip.Front in={!isShowingQRcode} disableAnimations={!mdUp}>
          <ShareInstitutionalPageWrapper onBack={onClose} onClose={onClose}>
            <>
              <Badge
                anchorOrigin={{
                  horizontal: 'right',
                  vertical: 'bottom',
                }}
                sx={theme => ({
                  background: `url("${
                    company.background_image || BannerDefault.src
                  }") no-repeat center / cover`,
                  height: '92px',
                  position: 'relative',
                  width: '100%',
                  [theme.breakpoints.up('md')]: {
                    borderRadius: 0,
                    height: '166px',
                  },
                  '& .MuiBadge-badge': {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  },
                })}
                badgeContent={
                  <Avatar
                    alt={`Logo da empresa ${company.full_name}`}
                    src={company.logo}
                    sx={{
                      backgroundColor: 'background.paper',
                      filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.25))',
                      height: '102px',
                      width: '102px',
                    }}
                  >
                    <StorefrontIcon
                      sx={{
                        color: '#C6C6C6',
                        height: '36px',
                        width: '40px',
                      }}
                    />
                  </Avatar>
                }
                role="banner"
              />

              <Box
                sx={theme => ({
                  backgroundColor: 'background.paper',
                  paddingTop: 9,
                  paddingX: 2,
                  [theme.breakpoints.up('md')]: {
                    borderRadius: '10px 10px 0 0',
                    marginTop: '-10px',
                    position: 'relative',
                  },
                })}
              >
                <Box
                  component="section"
                  textAlign="center"
                  textTransform="capitalize"
                >
                  <Typography component="h2" fontWeight="bold" variant="h5">
                    {(company.display_name || company.full_name).toLowerCase()}
                  </Typography>
                  {company.slogan && (
                    <Typography
                      color="text.secondary"
                      component="p"
                      variant="body2"
                    >
                      {company.slogan}
                    </Typography>
                  )}
                </Box>

                <Divider
                  variant="middle"
                  sx={{
                    margin: '16px auto',
                    width: '80%',
                  }}
                />

                <Stack component="section" marginBottom={3} spacing={1}>
                  {(company.state || company.street || company.city) && (
                    <RowInfos as="span">
                      <RoomOutlinedIcon fontSize="small" />
                      <Typography
                        component="p"
                        textTransform="capitalize"
                        variant="body2"
                      >
                        {formatCompanyAddress(company)}
                      </Typography>
                    </RowInfos>
                  )}
                  {company.email && (
                    <RowInfos as="span">
                      <EmailOutlinedIcon fontSize="small" />
                      <Typography component="p" variant="body2">
                        {company.email}
                      </Typography>
                    </RowInfos>
                  )}
                  {company.phone && (
                    <RowInfos as="span">
                      <LocalPhoneOutlinedIcon fontSize="small" />
                      <Typography component="p" variant="body2">
                        {phoneMask(company.phone)}
                      </Typography>
                    </RowInfos>
                  )}
                  {company.cnpj && (
                    <RowInfos as="span">
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography component="p" variant="body2">
                        CNPJ {cnpjMask(company.cnpj)}
                      </Typography>
                    </RowInfos>
                  )}
                  {company.website_url && (
                    <RowInfos as="span">
                      <LanguageOutlinedIcon fontSize="small" />
                      <Typography component="p" variant="body2">
                        {company.website_url}
                      </Typography>
                    </RowInfos>
                  )}
                </Stack>
              </Box>

              <Box
                component="div"
                onClick={generateQRCode}
                role="button"
                sx={{
                  alignItems: 'center',
                  backgroundColor: 'primary.main',
                  borderRadius: '20px 0 0 20px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  marginLeft: 'auto',
                  padding: 2,
                  width: '166px',
                  '&:hover': {
                    filter: 'brightness(0.9)',
                    transition: 'filter 250ms ease-out',
                  },
                }}
              >
                <Typography component="p" variant="subtitle2" marginRight={2}>
                  Gerar QR Code
                </Typography>
                <QrCodeScannerIcon />
              </Box>
            </>

            <Button
              onClick={copyInstitutionalUrl}
              startIcon={alreadyCopied ? <Check /> : <Share />}
              sx={theme => ({
                [theme.breakpoints.up('md')]: {
                  marginX: '38px',
                },
              })}
              variant="contained"
            >
              {alreadyCopied ? 'URL Copiada!' : 'Copiar URL'}
            </Button>
          </ShareInstitutionalPageWrapper>
        </Flip.Front>

        <Flip.Back in={isShowingQRcode} disableAnimations={!mdUp}>
          <ShareInstitutionalPageWrapper
            onClose={onClose}
            onBack={() => setView('COMPANY_INFOS')}
            isShowingQRcode={!!QRcode}
          >
            <Stack
              alignItems="center"
              flex={1}
              justifyContent="center"
              paddingTop={12}
            >
              <Box
                sx={{
                  display: 'grid',
                  height: '256px',
                  placeItems: 'center',
                  position: 'relative',
                  width: '256px',
                }}
              >
                <Image
                  src={QRCodeFrame}
                  alt="Uma moldura para o qrcode com os 4 cantos levemente arredondados e na cor cinza"
                  layout="fill"
                />

                <Box height="240px" position="relative" width="240px">
                  {QRcode && (
                    <Image
                      src={URL.createObjectURL(
                        new Blob([QRcode], { type: 'image/png' })
                      )}
                      alt=""
                      layout="fill"
                    />
                  )}
                </Box>
              </Box>

              <Divider
                variant="middle"
                sx={{
                  margin: '48px auto 16px',
                  width: '80%',
                }}
              />

              <Stack component="article" paddingX={2}>
                <Avatar
                  alt={`Logo da empresa ${company.full_name}`}
                  src={company.logo}
                  sx={{
                    backgroundColor: 'background.paper',
                    filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.25))',
                    height: '38px',
                    margin: '0 auto',
                    width: '38px',
                  }}
                >
                  <StorefrontIcon
                    sx={{
                      color: '#C6C6C6',
                    }}
                  />
                </Avatar>
                <Typography
                  component="h2"
                  fontWeight="bold"
                  marginTop="6px"
                  textAlign="center"
                  textTransform="capitalize"
                  variant="h6"
                >
                  {(company.display_name || company.full_name).toLowerCase()}
                </Typography>
              </Stack>
            </Stack>

            <Button
              onClick={downloadQRCode}
              sx={theme => ({
                [theme.breakpoints.up('md')]: {
                  marginX: '38px',
                },
              })}
              variant="contained"
            >
              Baixar QRCode
            </Button>
          </ShareInstitutionalPageWrapper>
        </Flip.Back>
      </>
    </Modal>
  );
};
